/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import { observable, reaction } from 'mobx';
import * as React from 'react';

import { INosCountryPickerData } from './nos-country-picker.data';
import { INosCountryPickerProps } from './nos-country-picker.props.autogenerated';
import BusinessSignUpDropdown from './components/sign-up-dropdown';
import { IGetAvailableLanguagesData } from '../../actions/get-available-languages.action';

/**
 * Interface for ICountryPickerExtendedProps.
 */
export interface ICountryPickerExtendedProps extends INosCountryPickerProps<INosCountryPickerData> {
    updateDropdown(newSelectValue: IGetAvailableLanguagesData): void;
    selectedValue: IGetAvailableLanguagesData;
    isSubmittedPressed: boolean;
}

/**
 * Interface for ICountryPickerState.
 */
export interface ICountryPickerState {
    countryId?: string;
}

/**
 * Interface for ICountryPickerViewProps.
 */
export interface ICountryPickerViewProps {
    className: string;
    CountryPicker: IModuleProps;
    availableCountries: any;
    // selectedCountry?: any;
}

/**
 *
 * Address component.
 * @extends {React.Component<INosCountryPickerProps<INosCountryPickerData>, ICountryPickerState>}
 */
class NosCountryPicker extends React.Component<ICountryPickerExtendedProps, ICountryPickerState> {
    @observable private availableCountries?: IGetAvailableLanguagesData[];

    public constructor(props: ICountryPickerExtendedProps) {
        super(props);

        const { data } = this.props;

        this.availableCountries = data.availableCountries.result || [];
    }

    public componentDidMount(): void {
        // Initializing data props
        this._dataInitialize(this.props);
    }

    public shouldComponentUpdate(nextProps: ICountryPickerExtendedProps, nextState: ICountryPickerState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element | null {
        const { renderView, config, selectedValue } = this.props;

        const viewProps = {
            ...this.props,
            className: config.className,
            CountryPicker: {
                moduleProps: this.props,
                className: classnames('ms-business-account-address', config.className)
            },
            availableCountries: this._initializeDropdown(this.availableCountries ?? [], selectedValue)
        };

        return renderView(viewProps) as React.ReactElement;
    }

    private readonly _initializeDropdown = (
        availableCountries: IGetAvailableLanguagesData[],
        selectedValue: IGetAvailableLanguagesData
    ) => {
        const tempOptions = availableCountries?.map(el => {
            return {
                key: el.key,
                value: el.value
            };
        });
        return (
            <BusinessSignUpDropdown
                className={``}
                isRequired={false}
                isSubmittedPressed={this.props.isSubmittedPressed}
                options={tempOptions}
                onChange={(event: any) => this._onInputChange(event, availableCountries)}
                selectValue={selectedValue}
                value={selectedValue?.value ?? ''}
                resources={this.props.resources}
            />
        );
    };

    private readonly _onInputChange = (event: React.ChangeEvent<HTMLSelectElement>, availableCountries: IGetAvailableLanguagesData[]) => {
        const newChosenLanguage = availableCountries.find(el => el.key === event.target.value);
        if (newChosenLanguage) {
            this.props.updateDropdown(newChosenLanguage);
        }
    };

    /**
     * Method data initialization.
     * @param props -The account management props.
     */
    private readonly _dataInitialize = (props: ICountryPickerExtendedProps): void => {
        const { data } = props;

        reaction(
            () => data.availableCountries.result,
            () => {
                this.availableCountries = data.availableCountries.result ?? [];
            }
        );
    };
}

export default NosCountryPicker;
